import React, { useState, useEffect, useRef, useCallback } from 'react';

const GoogleTranslateWidget = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('bs');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGoogleTranslateReady, setIsGoogleTranslateReady] = useState(false);
  const dropdownRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const readinessCheckRef = useRef(null);

  const languages = [
    { code: 'bs', name: 'Bosanski', flag: '🇧🇦' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  // Detect current language from Google Translate
  const detectCurrentLanguage = useCallback(() => {
    try {
      // Method 1: Check URL hash for Google Translate
      const hash = window.location.hash;
      if (hash.includes('googtrans')) {
        const match = hash.match(/googtrans\([^|]*\|([^)]*)\)/);
        if (match && match[1]) {
          const currentLang = match[1];
          if (currentLang !== selectedLanguage) {
            console.log(`Detected language from URL: ${currentLang}`);
            setSelectedLanguage(currentLang);
          }
          return currentLang;
        }
      }

      // Method 2: Check Google Translate select element
      const selectElement = document.querySelector('.goog-te-combo');
      if (selectElement && selectElement.value) {
        const value = selectElement.value;
        if (value.includes('|')) {
          const currentLang = value.split('|')[1];
          if (currentLang && currentLang !== selectedLanguage) {
            console.log(`Detected language from select: ${currentLang}`);
            setSelectedLanguage(currentLang);
          }
          return currentLang;
        }
      }

      // Method 3: Check for translated content indicators
      const translatedElements = document.querySelectorAll('[class*="translated"], [data-gt-translated]');
      if (translatedElements.length > 0) {
        console.log('Page appears to be translated, but language unclear');
      }

      return selectedLanguage;
    } catch (error) {
      console.warn('Error detecting current language:', error);
      return selectedLanguage;
    }
  }, [selectedLanguage]);

  // Add CSS to hide Google Translate banner
  useEffect(() => {
    const styleId = 'google-translate-custom-styles';
    
    const addGoogleTranslateStyles = () => {
      // Remove existing styles if any
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      // Add new styles
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Hide Google Translate banner/bar */
        .goog-te-banner-frame,
        .goog-te-banner-frame.skiptranslate,
        .goog-te-ftab-frame,
        .goog-te-ftab-frame.skiptranslate {
          display: none !important;
        }

        /* Hide Google Translate balloon */
        .goog-te-balloon-frame {
          display: none !important;
        }

        /* Prevent body margin/padding changes from Google Translate */
        body {
          top: 0 !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
        }

        /* Hide other Google Translate UI elements */
        .goog-logo-link {
          display: none !important;
        }

        .goog-te-gadget-icon {
          display: none !important;
        }

        /* Style the menu if it appears */
        .goog-te-menu-frame {
          z-index: 10000 !important;
        }

        .goog-te-menu2 {
          border: 1px solid var(--border-color) !important;
          background: var(--input-bg) !important;
          color: var(--text-color) !important;
        }

        .goog-te-menu2-item {
          color: var(--text-color) !important;
          background: var(--input-bg) !important;
        }

        .goog-te-menu2-item:hover {
          background: var(--bg-color) !important;
        }
      `;
      
      document.head.appendChild(style);
    };

    addGoogleTranslateStyles();

    // Also listen for DOM changes to catch dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if Google Translate added banner elements
            if (node.className && typeof node.className === 'string' && 
                node.className.includes('goog-te-banner')) {
              node.style.display = 'none';
            }
            
            // Also check child elements
            const bannerElements = node.querySelectorAll?.('.goog-te-banner-frame, .goog-te-ftab-frame');
            bannerElements?.forEach(el => {
              el.style.display = 'none';
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if Google Translate is ready
  const checkGoogleTranslateReadiness = useCallback(() => {
    console.log('Checking Google Translate readiness...');
    
    // Log all Google Translate related elements for debugging
    const allGoogElements = document.querySelectorAll('[class*="goog"]');
    console.log(`Found ${allGoogElements.length} elements with 'goog' in class name:`, 
      Array.from(allGoogElements).map(el => ({ tag: el.tagName, class: el.className, id: el.id })));
    
    // Check for various Google Translate selectors
    const selectors = [
      '.goog-te-combo',
      'select.goog-te-combo', 
      '.goog-te-menu-value',
      '.goog-te-banner-frame',
      '.goog-te-menu',
      '.skiptranslate',
      '#google_translate_element select',
      '#google_translate_element .goog-te-gadget'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`Found element with selector: ${selector}`, element);
        
        // Special check for select elements
        if (element.tagName === 'SELECT' && element.options) {
          console.log(`Select element has ${element.options.length} options:`, 
            Array.from(element.options).map(opt => ({ value: opt.value, text: opt.text })));
          
          if (element.options.length > 1) {
            setIsGoogleTranslateReady(true);
            console.log('Google Translate is ready for language changes (select found)');
            
            // Detect current language when ready
            detectCurrentLanguage();
            
            if (readinessCheckRef.current) {
              clearInterval(readinessCheckRef.current);
              readinessCheckRef.current = null;
            }
            return true;
          }
        }
        
        // Check for any Google Translate widget
        if (selector.includes('gadget') || selector.includes('menu')) {
          setIsGoogleTranslateReady(true);
          console.log('Google Translate is ready for language changes (widget found)');
          
          // Detect current language when ready
          detectCurrentLanguage();
          
          if (readinessCheckRef.current) {
            clearInterval(readinessCheckRef.current);
            readinessCheckRef.current = null;
          }
          return true;
        }
      }
    }
    
    // Also check if the google translate element has any content
    const gtElement = document.getElementById('google_translate_element');
    if (gtElement && gtElement.innerHTML.trim()) {
      console.log('Google Translate element has content:', gtElement.innerHTML);
      
      // Look for any select or clickable elements inside
      const innerSelect = gtElement.querySelector('select');
      const innerClickable = gtElement.querySelector('[onclick], [onchange], button, .goog-te-menu-value');
      
      if (innerSelect || innerClickable) {
        setIsGoogleTranslateReady(true);
        console.log('Google Translate is ready (found interactive elements)');
        
        // Detect current language when ready
        detectCurrentLanguage();
        
        if (readinessCheckRef.current) {
          clearInterval(readinessCheckRef.current);
          readinessCheckRef.current = null;
        }
        return true;
      }
    }
    
    console.log('Google Translate not ready yet - no interactive elements found');
    return false;
  }, [detectCurrentLanguage]);

  const initializeGoogleTranslate = useCallback(() => {
    // Skip if already loaded
    if (scriptLoadedRef.current) {
      return;
    }

    try {
      // Check if script already exists
      if (document.querySelector('script[src*="translate.google.com"]')) {
        scriptLoadedRef.current = true;
        // Start checking for readiness
        readinessCheckRef.current = setInterval(checkGoogleTranslateReadiness, 1000);
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      
      // Set up the callback function
      window.googleTranslateElementInit = () => {
        try {
          if (window.google && window.google.translate && window.google.translate.TranslateElement) {
            new window.google.translate.TranslateElement({
              pageLanguage: 'bs',
              includedLanguages: 'bs,en,de,ar,tr',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false,
              multilanguagePage: true
            }, 'google_translate_element');
            
            scriptLoadedRef.current = true;
            console.log('Google Translate initialized successfully');
            
            // Start checking for readiness every second
            readinessCheckRef.current = setInterval(checkGoogleTranslateReadiness, 1000);
          }
        } catch (error) {
          console.warn('Google Translate initialization error:', error);
        }
      };

      // Add script to head
      document.head.appendChild(script);
      
      // Handle script load errors
      script.onerror = () => {
        console.warn('Failed to load Google Translate script');
      };
      
    } catch (error) {
      console.warn('Error setting up Google Translate:', error);
    }
  }, [checkGoogleTranslateReadiness]);

  useEffect(() => {
    // Initialize Google Translate after component mounts
    const timer = setTimeout(() => {
      initializeGoogleTranslate();
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (readinessCheckRef.current) {
        clearInterval(readinessCheckRef.current);
      }
    };
  }, [initializeGoogleTranslate]);

  // Periodically check for language changes
  useEffect(() => {
    if (isGoogleTranslateReady) {
      const languageCheckInterval = setInterval(() => {
        detectCurrentLanguage();
      }, 2000); // Check every 2 seconds

      return () => clearInterval(languageCheckInterval);
    }
  }, [isGoogleTranslateReady, detectCurrentLanguage]);

  // Listen for URL hash changes (Google Translate updates)
  useEffect(() => {
    const handleHashChange = () => {
      if (isGoogleTranslateReady) {
        setTimeout(() => {
          detectCurrentLanguage();
        }, 100);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isGoogleTranslateReady, detectCurrentLanguage]);

  const triggerGoogleTranslate = useCallback((langCode) => {
    console.log(`Attempting to change language to: ${langCode}`);
    
    // Special handling for Bosnian - reset to original language
    if (langCode === 'bs') {
      console.log('Resetting to original language (Bosnian)');
      try {
        // Method 1: Use Google Translate's restore function if available
        if (window.google && window.google.translate) {
          try {
            // Try to find and call the restore function
            const frames = document.querySelectorAll('iframe');
            frames.forEach(frame => {
              try {
                if (frame.src && frame.src.includes('translate.google')) {
                  frame.contentWindow.postMessage('restore', '*');
                }
              } catch (e) {
                // Ignore cross-origin errors
              }
            });

            // Try calling restore on the translate element directly
            const translateElement = window.google.translate.TranslateElement;
            if (translateElement && translateElement.getInstance) {
              const instance = translateElement.getInstance();
              if (instance && instance.restore) {
                instance.restore();
                console.log('Called Google Translate restore function');
                return true;
              }
            }
          } catch (error) {
            console.log('Google Translate restore failed:', error);
          }
        }

        // Method 2: Reset via select element to empty/default value
        const selectElement = document.querySelector('.goog-te-combo');
        if (selectElement && selectElement.options) {
          console.log('Found select element with', selectElement.options.length, 'options');
          
          // Try to find and select the default option (empty value or first option)
          for (let i = 0; i < selectElement.options.length; i++) {
            const option = selectElement.options[i];
            console.log(`Checking option ${i}: value="${option.value}", text="${option.text}"`);
            
            // Look for empty value, 'auto', or the very first option
            if (option.value === '' || option.value === 'auto' || i === 0) {
              console.log(`Selecting reset option at index ${i}: "${option.value}"`);
              selectElement.selectedIndex = i;
              
              // Fire multiple events to ensure Google Translate responds
              const events = ['change', 'input', 'blur', 'focus', 'click'];
              events.forEach(eventType => {
                const event = new Event(eventType, { bubbles: true, cancelable: true });
                selectElement.dispatchEvent(event);
              });
              
              // Also try manual trigger
              if (selectElement.onchange) {
                selectElement.onchange();
              }
              
              console.log('Fired reset events on select element');
              break;
            }
          }
        }

        // Method 3: Clear all Google Translate URL parameters and cookies
        console.log('Clearing Google Translate state...');
        
        // Clear URL hash
        if (window.location.hash.includes('googtrans')) {
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, '', cleanUrl);
          console.log('Cleared googtrans from URL hash');
        }

        // Clear Google Translate cookies
        try {
          const cookies = ['googtrans', 'googtrans_domain', 'google_translate_element'];
          cookies.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          console.log('Cleared Google Translate cookies');
        } catch (error) {
          console.log('Error clearing cookies:', error);
        }

        // Method 4: Try to click "Show original" link if it exists
        const showOriginalLinks = document.querySelectorAll('a');
        for (const link of showOriginalLinks) {
          if (link.href && link.href.includes('translate.google') && link.href.includes('restore')) {
            console.log('Clicking show original link');
            link.click();
            return true;
          }
          
          const text = link.textContent.toLowerCase();
          if (text.includes('show original') || text.includes('prikaži original') || text.includes('original')) {
            console.log('Clicking show original text link:', text);
            link.click();
            return true;
          }
        }

        // Method 5: Force reload as last resort
        console.log('Forcing page reload to reset translation');
        setTimeout(() => {
          // Make sure URL is clean before reload
          const cleanUrl = window.location.pathname + window.location.search;
          if (window.location.href !== window.location.origin + cleanUrl) {
            window.location.href = window.location.origin + cleanUrl;
          } else {
            window.location.reload();
          }
        }, 500);
        
        return true;
        
      } catch (error) {
        console.warn('Error resetting to original language:', error);
        // Force reload as fallback
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      return true;
    }
    
    try {
      // Method 1: Standard select approach with better debugging
      const selectElement = document.querySelector('.goog-te-combo');
      console.log('Select element found:', selectElement);
      
      if (selectElement && selectElement.options) {
        console.log('Select options available:', selectElement.options.length);
        console.log('Current value:', selectElement.value);
        
        // Log all available options
        for (let i = 0; i < selectElement.options.length; i++) {
          const option = selectElement.options[i];
          console.log(`Option ${i}: value="${option.value}", text="${option.text}"`);
          
          if (option.value.includes(langCode) || option.value.includes(`|${langCode}`) || option.value === langCode) {
            console.log(`Found matching option at index ${i}:`, option.value);
            selectElement.selectedIndex = i;
            
            // Try multiple event types
            const events = ['change', 'input', 'click'];
            events.forEach(eventType => {
              const event = new Event(eventType, { bubbles: true, cancelable: true });
              selectElement.dispatchEvent(event);
            });
            
            console.log(`Language changed to ${langCode} via select element`);
            return true;
          }
        }
        
        console.log(`No matching option found for language code: ${langCode}`);
      }

      // Method 2: Try to find and click language links/buttons
      const gtElement = document.getElementById('google_translate_element');
      if (gtElement) {
        const clickableElements = gtElement.querySelectorAll('a, button, [onclick], .goog-te-menu-value');
        console.log(`Found ${clickableElements.length} clickable elements in GT widget`);
        
        clickableElements.forEach((element, index) => {
          console.log(`Clickable element ${index}:`, {
            tag: element.tagName,
            text: element.textContent,
            href: element.href,
            onclick: element.onclick ? 'present' : 'none'
          });
        });
        
        // Try to click the main menu element
        const menuValue = gtElement.querySelector('.goog-te-menu-value');
        if (menuValue) {
          console.log('Clicking menu value element...');
          menuValue.click();
          
          // Wait a bit and try to find language options
          setTimeout(() => {
            const langOptions = document.querySelectorAll('.goog-te-menu2-item span');
            console.log(`Found ${langOptions.length} language options in menu`);
            
            langOptions.forEach((option, index) => {
              const text = option.textContent.toLowerCase();
              console.log(`Language option ${index}: "${text}"`);
              
              // Map language codes to likely text matches
              const langMappings = {
                'en': ['english', 'engleski'],
                'de': ['german', 'deutsch', 'njemački', 'nemački'],
                'ar': ['arabic', 'عربي', 'arapski'],
                'tr': ['turkish', 'türkçe', 'turski'],
                'bs': ['bosnian', 'bosanski']
              };
              
              const possibleMatches = langMappings[langCode] || [langCode];
              if (possibleMatches.some(match => text.includes(match.toLowerCase()))) {
                console.log(`Clicking language option: ${text}`);
                option.click();
                return true;
              }
            });
          }, 500);
        }
      }

      // Method 3: Try Google Translate API directly if available
      if (window.google && window.google.translate) {
        console.log('Trying Google Translate API directly...');
        try {
          // Try to access the translate instance
          const translateInstance = window.google.translate.TranslateElement.getInstance();
          if (translateInstance) {
            console.log('Found translate instance, attempting to change language');
            // This might work with some versions
            translateInstance.c(langCode);
            return true;
          }
        } catch (apiError) {
          console.log('Direct API method failed:', apiError);
        }
      }

      // Method 4: URL-based approach as last resort
      console.log('Trying URL-based translation...');
      const currentUrl = window.location.href;
      
      // Remove any existing translate parameters
      let cleanUrl = currentUrl.split('#')[0].split('?')[0];
      
      // Try to add Google Translate URL parameter
      window.history.replaceState({}, '', `${cleanUrl}#googtrans(bs|${langCode})`);
      console.log(`Added URL hash for translation: #googtrans(bs|${langCode})`);
      
      // Trigger a page refresh of Google Translate
      if (window.google && window.google.translate) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

      return false;
    } catch (error) {
      console.warn('Error in triggerGoogleTranslate:', error);
      return false;
    }
  }, []);

  const handleLanguageSelect = (langCode) => {
    setSelectedLanguage(langCode);
    setIsDropdownOpen(false);
    
    console.log(`Language selected: ${langCode}`);
    console.log(`Google Translate ready: ${isGoogleTranslateReady}`);
    
    if (!isGoogleTranslateReady) {
      console.log('Google Translate not ready yet, will retry...');
      // Retry after a delay
      setTimeout(() => {
        if (checkGoogleTranslateReadiness()) {
          triggerGoogleTranslate(langCode);
        } else {
          console.log('Google Translate still not ready after retry');
        }
      }, 2000);
      return;
    }
    
    // Try to trigger the language change
    const success = triggerGoogleTranslate(langCode);
    if (!success) {
      console.log('Primary method failed, trying alternative approach...');
      // Try alternative approach after delay
      setTimeout(() => triggerGoogleTranslate(langCode), 1000);
    }
  };

  const selectedLang = languages.find(lang => lang.code === selectedLanguage);

  return (
    <div className="google-translate-widget">
      <div className="translate-header">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M5 8l6 6"></path>
          <path d="M4 14l6-6 2-3"></path>
          <path d="M2 5h12"></path>
          <path d="M7 2h1"></path>
          <path d="M22 22l-5-10-5 10"></path>
          <path d="M14 18h6"></path>
        </svg>
        {isGoogleTranslateReady && (
          <span style={{ fontSize: '10px', color: 'green', marginLeft: '4px' }}>●</span>
        )}
      </div>
      
      <div className="language-selector" ref={dropdownRef}>
        <button 
          className="language-button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-label="Select language"
        >
          <span className="flag">{selectedLang?.flag}</span>
          <span className="language-name">{selectedLang?.name}</span>
          <svg 
            className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
            xmlns="http://www.w3.org/2000/svg" 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        
        {isDropdownOpen && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`language-option ${selectedLanguage === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageSelect(lang.code)}
                aria-label={`Select ${lang.name}`}
              >
                <span className="flag">{lang.flag}</span>
                <span className="language-name">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" style={{ display: 'none' }}></div>
    </div>
  );
};

export default GoogleTranslateWidget; 